/* eslint-disable react/prop-types */

import React, { useState, useCallback, useEffect } from 'react';
import {
	Box,
	Grid,
	Typography,
	Paper,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	useTheme,
	useMediaQuery,
	CircularProgress,
	TextField,
	Button,
	FormControlLabel,
	Switch,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { getSalesOrderCalendar } from '../../services';
import { fetchUserSummaryCached } from '../../userSummaryCache';
import { IUserSummary, ICalendarDayData } from '../../types';
import { extractErrorMessage } from 'features/quotation/helpers';
import { SalesOrderCalendarCard } from './SalesOrderCalendarCard';
import { isOperatorOrHigher } from 'features/auth/helpers';

export const SalesOrderCalendar = (): JSX.Element => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const { auth } = useSelector((state: RootState) => state);

	// Estados para usuarios y teams
	const [users, setUsers] = useState<IUserSummary[]>([]);
	const [teams, setTeams] = useState<string[]>([]);
	const [selectedUser, setSelectedUser] = useState<string>(''); // Ahora guardamos el ID del usuario
	const [selectedTeam, setSelectedTeam] = useState<string>('');
	const [showBillableCustomer, setShowBillableCustomer] = useState<boolean>(false); // false = pasajero principal, true = cliente facturable

	// Estados para filtros (solo se usan cuando se hace clic en "Aplicar")
	const [dateFrom, setDateFrom] = useState<string>(() => {
		const today = new Date();
		return (
			today.getFullYear() +
			'-' +
			String(today.getMonth() + 1).padStart(2, '0') +
			'-' +
			String(today.getDate()).padStart(2, '0')
		);
	});
	const [dateTo, setDateTo] = useState<string>(() => {
		const date = new Date();
		date.setDate(date.getDate() + 30);
		return (
			date.getFullYear() +
			'-' +
			String(date.getMonth() + 1).padStart(2, '0') +
			'-' +
			String(date.getDate()).padStart(2, '0')
		);
	});

	// Estados para datos del calendario
	const [calendarData, setCalendarData] = useState<ICalendarDayData[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [appliedFilters, setAppliedFilters] = useState<{
		dateFrom: string;
		dateTo: string;
		selectedUser: string;
		selectedTeam: string;
	} | null>(null);

	// Cargar usuarios y calcular teams
	const loadUsers = useCallback(async () => {
		try {
			const userData = await fetchUserSummaryCached();

			// Si el usuario es operator o superior, mostrar todos los usuarios
			// Si no, mostrar solo usuarios activos
			const usersToShow = isOperatorOrHigher(auth.user) ? userData : userData.filter((user) => user.active === true);
			setUsers(usersToShow);

			// Calcular teams únicos (basado en usuarios mostrados)
			const teamSet = new Set<string>();
			usersToShow.forEach((user) => {
				if (user.team) {
					teamSet.add(user.team);
				}
			});
			setTeams(Array.from(teamSet));

			// Establecer el usuario actual por defecto (si está en la lista de usuarios mostrados)
			const currentUser = usersToShow.find((user) => user.username === auth.user?.username);
			if (currentUser) {
				setSelectedUser(currentUser.id.toString());
			}
		} catch (err) {
			setError(extractErrorMessage(err, 'Error al cargar usuarios'));
		}
	}, [auth.user?.username, auth.user]);

	// Generar semanas completas para el calendario
	const generateCalendarWeeks = useCallback(() => {
		const startDate = new Date(dateFrom + 'T00:00:00');
		const endDate = new Date(dateTo + 'T00:00:00');

		// Encontrar el lunes de la primera semana
		const firstDay = new Date(startDate);
		const dayOfWeek = firstDay.getDay();
		const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Si es domingo (0), retroceder 6 días
		firstDay.setDate(firstDay.getDate() + mondayOffset);

		// Encontrar el domingo de la última semana
		const lastDay = new Date(endDate);
		const lastDayOfWeek = lastDay.getDay();
		const sundayOffset = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek; // Si es domingo (0), no mover
		lastDay.setDate(lastDay.getDate() + sundayOffset);

		// Generar todas las semanas completas
		const weeks = [];
		const current = new Date(firstDay);

		while (current <= lastDay) {
			const week = [];
			for (let i = 0; i < 7; i++) {
				week.push(new Date(current));
				current.setDate(current.getDate() + 1);
			}
			weeks.push(week);
		}

		return { weeks, startDate, endDate };
	}, [dateFrom, dateTo]);

	// Cargar datos del calendario
	const loadCalendarData = useCallback(async () => {
		try {
			setLoading(true);
			setError('');

			// Las fechas ya están en formato YYYY-MM-DD, no necesitan conversión
			const dateFromStr = dateFrom;
			const dateToStr = dateTo;

			// Construir createdByIds basado en filtros (usando IDs)
			let createdByIds = '';
			if (selectedTeam && selectedTeam !== '') {
				// Filtrar por team: obtener todos los IDs del team
				const teamUsers = users.filter((user) => user.team === selectedTeam);
				createdByIds = teamUsers.map((user) => user.id.toString()).join(',');
			} else if (selectedUser && selectedUser !== '' && selectedUser !== 'TODOS') {
				// Filtrar por usuario individual (si no es "TODOS")
				createdByIds = selectedUser;
			}
			// Si selectedUser === 'TODOS' o está vacío, createdByIds queda vacío (sin filtros)

			const response = await getSalesOrderCalendar(dateFromStr, dateToStr, createdByIds);
			setCalendarData(response.data);

			// Guardar los filtros que realmente se aplicaron
			setAppliedFilters({
				dateFrom,
				dateTo,
				selectedUser,
				selectedTeam,
			});
		} catch (err) {
			setError(extractErrorMessage(err, 'Error al cargar los datos del calendario'));
		} finally {
			setLoading(false);
		}
	}, [dateFrom, dateTo, selectedUser, selectedTeam, users]);

	// Effect para cargar usuarios al inicio
	useEffect(() => {
		loadUsers();
	}, [loadUsers]);

	// Ejecutar búsqueda automática al cargar (con valores por defecto)
	useEffect(() => {
		if (users.length > 0 && selectedUser && !appliedFilters) {
			// Solo ejecutar si tenemos usuarios, usuario seleccionado y no hemos aplicado filtros aún
			loadCalendarData();
		}
	}, [users.length, selectedUser, appliedFilters, loadCalendarData]);

	// Función para generar el texto descriptivo de filtros aplicados
	const getFilterDescription = () => {
		if (!appliedFilters) return '';

		const parts = [];

		// Fechas
		const fromFormatted = new Date(appliedFilters.dateFrom + 'T00:00:00').toLocaleDateString('es-ES');
		const toFormatted = new Date(appliedFilters.dateTo + 'T00:00:00').toLocaleDateString('es-ES');
		parts.push(`Del ${fromFormatted} al ${toFormatted}`);

		// Usuario/Team
		if (appliedFilters.selectedTeam && appliedFilters.selectedTeam !== '') {
			parts.push(`Equipo: ${appliedFilters.selectedTeam}`);
		} else if (
			appliedFilters.selectedUser &&
			appliedFilters.selectedUser !== '' &&
			appliedFilters.selectedUser !== 'TODOS'
		) {
			const user = users.find((u) => u.id.toString() === appliedFilters.selectedUser);
			parts.push(`Usuario: ${user?.name || appliedFilters.selectedUser}`);
		} else {
			parts.push('Todos los usuarios');
		}

		return parts.join(' • ');
	};

	const handleUserChange = (event: any) => {
		setSelectedUser(event.target.value);
		setSelectedTeam(''); // Limpiar team cuando se selecciona usuario
	};

	const handleTeamChange = (event: any) => {
		setSelectedTeam(event.target.value);
		setSelectedUser(''); // Limpiar usuario cuando se selecciona team
	};

	// Handlers simples - solo actualizar valores
	const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setDateFrom(event.target.value);
	};

	const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setDateTo(event.target.value);
	};

	// Aplicar filtros y ejecutar búsqueda
	const handleApplyFilters = () => {
		if (users.length > 0) {
			loadCalendarData();
		}
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100%', mx: 'auto', px: 2 }}>
			{/* Título */}
			<Typography variant="h5" sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold' }}>
				CALENDARIO DE ÓRDENES DE VENTA
			</Typography>

			{/* Filtros */}
			<Paper sx={{ p: 2, mb: 3 }}>
				<Grid container spacing={2} alignItems="center">
					{/* Filtros de fecha */}
					<Grid item xs={12} sm={6} md={2}>
						<TextField
							label="Fecha desde"
							type="date"
							value={dateFrom}
							onChange={handleDateFromChange}
							fullWidth
							size="small"
							InputLabelProps={{
								shrink: true,
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<TextField
							label="Fecha hasta"
							type="date"
							value={dateTo}
							onChange={handleDateToChange}
							fullWidth
							size="small"
							InputLabelProps={{
								shrink: true,
							}}
						/>
					</Grid>

					{/* Filtro por usuario */}
					<Grid item xs={12} sm={6} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel>Usuario</InputLabel>
							<Select value={selectedUser} onChange={handleUserChange} label="Usuario">
								<MenuItem value="TODOS">TODOS</MenuItem>
								{users.map((user) => (
									<MenuItem key={user.id} value={user.id.toString()}>
										{user.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>

					{/* Filtro por team */}
					<Grid item xs={12} sm={6} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel>Equipo</InputLabel>
							<Select value={selectedTeam} onChange={handleTeamChange} label="Equipo">
								<MenuItem value="">Sin filtro</MenuItem>
								{teams.map((team) => (
									<MenuItem key={team} value={team}>
										{team}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>

					{/* Botón Aplicar */}
					<Grid item xs={12} sm={6} md={2}>
						<Button variant="contained" onClick={handleApplyFilters} fullWidth size="medium" disabled={loading}>
							{loading ? 'Buscando...' : 'Aplicar'}
						</Button>
					</Grid>

					{/* Toggle para mostrar pasajero principal o cliente facturable */}
					<Grid item xs={12} md={2}>
						<FormControlLabel
							control={
								<Switch
									checked={showBillableCustomer}
									onChange={(e) => setShowBillableCustomer(e.target.checked)}
									color="primary"
									size="small"
								/>
							}
							label={
								<Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
									{showBillableCustomer ? 'Cliente facturable' : 'Pasajero principal'}
								</Typography>
							}
							sx={{
								ml: 0,
								justifyContent: 'center',
								'& .MuiFormControlLabel-label': {
									fontSize: '0.85rem',
								},
							}}
						/>
					</Grid>
				</Grid>
			</Paper>

			{/* Descripción de filtros aplicados */}
			{appliedFilters && calendarData.length > 0 && (
				<Paper sx={{ p: 1.5, mb: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
					<Typography variant="body2" color="info.dark" sx={{ fontWeight: 500 }}>
						📅 Mostrando: {getFilterDescription()}
					</Typography>
				</Paper>
			)}

			{/* Error */}
			{error && (
				<Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
					<Typography>{error}</Typography>
				</Paper>
			)}

			{/* Mensaje inicial */}
			{!appliedFilters && calendarData.length === 0 && !loading && !error && (
				<Paper sx={{ p: 4, mb: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
					<Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
						Selecciona los filtros y haz clic en &quot;Aplicar&quot; para ver el calendario
					</Typography>
					<Typography variant="body2" color="text.secondary">
						📅 Configura las fechas y filtros de usuario arriba
					</Typography>
				</Paper>
			)}

			{/* Calendario solo se muestra cuando hay filtros aplicados y datos */}
			{appliedFilters && calendarData.length > 0 && (
				<>
					{/* Encabezados de días de la semana */}
					<Grid container spacing={1} sx={{ mb: 1 }}>
						{['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((dayName) => (
							<Grid item xs key={dayName}>
								<Typography
									variant="h6"
									sx={{
										textAlign: 'center',
										fontWeight: 'bold',
										color: 'primary.main',
										py: 1,
									}}
								>
									{dayName}
								</Typography>
							</Grid>
						))}
					</Grid>

					{/* Calendario - Vista dinámica basada en fechas */}
					{(() => {
						const { weeks, startDate, endDate } = generateCalendarWeeks();

						return weeks.map((week, weekIndex) => (
							<Grid container spacing={1} key={weekIndex} sx={{ mb: 2 }}>
								{week.map((day: Date, dayIndex: number) => {
									// Verificar si el día está dentro del rango filtrado
									const isInRange = day >= startDate && day <= endDate;

									// Formatear fecha para buscar en calendarData
									const dayStr = day.toISOString().split('T')[0];
									const dayData = calendarData.find((data) => data.travelDepartureDate === dayStr);
									const dayOrders = isInRange ? dayData?.saleOrders || [] : [];
									const isToday = day.toDateString() === new Date().toDateString();
									const today = new Date();
									today.setHours(0, 0, 0, 0);
									const isPastDate = day.getTime() < today.getTime();

									return (
										<Grid item xs key={dayIndex}>
											<Paper
												sx={{
													p: isSmallScreen ? 0.5 : 1,
													height: isSmallScreen ? 140 : isMobile ? 160 : 180,
													bgcolor: !isInRange
														? 'grey.50'
														: isToday
														? 'primary.light'
														: isPastDate
														? 'grey.100'
														: 'white',
													border: isToday && isInRange ? '2px solid' : '1px solid',
													borderColor: isToday && isInRange ? 'primary.main' : 'grey.300',
													opacity: !isInRange ? 0.3 : isPastDate ? 0.6 : 1,
													display: 'flex',
													flexDirection: 'column',
													overflow: 'hidden',
													position: 'relative',
												}}
											>
												<Typography
													variant="body2"
													sx={{
														fontWeight: isToday && isInRange ? 'bold' : 'normal',
														textAlign: 'center',
														mb: 0.5,
														color: !isInRange ? 'text.disabled' : isToday ? 'primary.contrastText' : 'text.primary',
														flex: '0 0 auto',
														fontSize: isSmallScreen ? '0.85rem' : '1rem',
													}}
												>
													{day.getDate()}
													{day.getDate() === 1 && (
														<Typography
															component="span"
															variant="caption"
															sx={{
																display: 'block',
																fontSize: isSmallScreen ? '0.6rem' : '0.7rem',
																color: !isInRange
																	? 'text.disabled'
																	: isToday
																	? 'primary.contrastText'
																	: 'text.secondary',
																fontWeight: 'normal',
															}}
														>
															{day.toLocaleDateString('es-ES', { month: 'short' })}
														</Typography>
													)}
												</Typography>

												{/* Container con scroll */}
												<Box
													sx={{
														flex: '1 1 auto',
														overflowY: 'auto',
														overflowX: 'hidden',
														display: 'flex',
														flexDirection: 'column',
														gap: 0.5,
														pr: 0.25,
														pt: dayOrders.length > 0 ? 1.25 : 0,
														'&::-webkit-scrollbar': {
															width: '4px',
														},
														'&::-webkit-scrollbar-track': {
															background: 'transparent',
														},
														'&::-webkit-scrollbar-thumb': {
															background: 'rgba(0,0,0,0.2)',
															borderRadius: '2px',
														},
														'&::-webkit-scrollbar-thumb:hover': {
															background: 'rgba(0,0,0,0.4)',
														},
													}}
												>
													{/* Mostrar las órdenes */}
													{dayOrders.map((order) => (
														<SalesOrderCalendarCard
															key={order.saleOrderId}
															order={order}
															showBillableCustomer={showBillableCustomer}
														/>
													))}
												</Box>
											</Paper>
										</Grid>
									);
								})}
							</Grid>
						));
					})()}

					{/* Resumen */}
					<Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
						<Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
							Resumen
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Total de órdenes mostradas:{' '}
							<strong>{calendarData.reduce((acc, day) => acc + day.saleOrders.length, 0)}</strong>
						</Typography>
						{calendarData.some((day) => day.saleOrders.some((order) => order.pendingPayAmount > 0)) && (
							<Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
								⚠️ Órdenes con saldo pendiente:{' '}
								<strong>
									{calendarData.reduce(
										(acc, day) => acc + day.saleOrders.filter((order) => order.pendingPayAmount > 0).length,
										0,
									)}
								</strong>
							</Typography>
						)}
					</Paper>
				</>
			)}
		</Box>
	);
};
